import { RefObject, useCallback, useMemo, useRef } from 'react';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { useColumnFilterRegistryContext } from '../context/useColumnFilterRegistryContext';
import { canOpenSortedColumnFilter } from '../../utils/shortcuts/canOpenSortedColumnFilter';
import { getFirstSortedFilterableColumn } from '../../utils/shortcuts/getFirstSortedFilterableColumn';
import { isSpaceOnSortableHeader } from '../../utils/shortcuts/isSpaceOnSortableHeader';
import { scrollGridToColumnSmooth } from '../../utils/shortcuts/scrollGridToColumnSmooth';
import { GridColumn } from '../../components/Manager/Grid/types';

const TRIGGER_DEBOUNCE_MS = 150;

type UseQuickColumnFilterShortcutArgs = {
  columns: GridColumn[];
  multiSortMeta: { field?: string }[] | undefined;
  gridRef: RefObject<HTMLElement | any>;
  // DOM root used by the shortcut dispatcher to decide whether this grid owns the keydown; usually matches gridRef but may be a wider wrapper.
  scopeRef?: RefObject<HTMLElement | null>;
  // While the grid is loading/fetching, the trigger is ignored so typing can't open a filter over stale or missing data.
  disabled?: boolean;
  shortcutId: string;
  // Claim the event even when this grid has no sorted filterable column. Used by slave subsets so the shortcut does not bubble up to the master grid when the slave has nothing to open.
  claimWhenEmpty?: boolean;
};

// Opens the column filter overlay for the first sorted + filterable column when the user starts typing inside the grid.
export const useQuickColumnFilterShortcut = ({
  columns,
  multiSortMeta,
  gridRef,
  scopeRef,
  disabled,
  shortcutId,
  claimWhenEmpty = false,
}: UseQuickColumnFilterShortcutArgs) => {
  const { openColumnFilter, appendPendingInitialChar } =
    useColumnFilterRegistryContext();

  // Last trigger timestamp; prevents accidental rapid taps from stacking overlay open requests.
  const lastTriggerAtRef = useRef(0);

  const firstSortedFilterableColumn = useMemo(
    () => getFirstSortedFilterableColumn(columns, multiSortMeta),
    [columns, multiSortMeta]
  );

  // Handle the key on a printable char over this grid when not loading. By default we also require a sorted filterable column so the event falls through to an outer grid (slave with no filter → master); `claimWhenEmpty` disables that fallthrough and lets the grid swallow the event silently.
  const shouldHandle = useCallback(
    (event: KeyboardEvent) =>
      !disabled &&
      (claimWhenEmpty || Boolean(firstSortedFilterableColumn)) &&
      canOpenSortedColumnFilter(event, gridRef.current),
    [disabled, claimWhenEmpty, firstSortedFilterableColumn, gridRef]
  );

  // First keypress scrolls to the column and opens the overlay; further keypresses while it opens just append to the typed buffer. Space is special — opens without injecting any char (mode handling lands in step 2).
  const onTrigger = useCallback(
    (event: KeyboardEvent) => {
      if (!firstSortedFilterableColumn) return;

      const isSpace = event.key === ' ';

      // Space never appends to the buffer (would type a space into the input); other chars use the buffer while the overlay is opening.
      if (
        !isSpace &&
        appendPendingInitialChar(firstSortedFilterableColumn.name, event.key)
      ) {
        event.preventDefault();
        return;
      }

      const now = Date.now();
      if (now - lastTriggerAtRef.current < TRIGGER_DEBOUNCE_MS) return;
      lastTriggerAtRef.current = now;

      event.preventDefault();

      scrollGridToColumnSmooth(
        gridRef,
        firstSortedFilterableColumn.name,
        () => {
          openColumnFilter(
            firstSortedFilterableColumn.name,
            isSpace ? undefined : event.key
          );
        }
      );
    },
    [
      appendPendingInitialChar,
      firstSortedFilterableColumn,
      gridRef,
      openColumnFilter,
    ]
  );

  // Opens the quick filter (no char injected) unless the grid is loading or modifiers are held; the dispatcher has already swallowed the event either way, so sorting never toggles.
  const onSortHeaderSpace = useCallback(
    (event: KeyboardEvent) => {
      if (disabled || event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      onTrigger(event);
    },
    [disabled, onTrigger]
  );

  useKeyboardShortcut({
    id: `${shortcutId}-${firstSortedFilterableColumn?.name ?? 'none'}`,
    scopeRef: scopeRef ?? gridRef,
    shouldHandle,
    onTrigger,
  });

  // PrimeReact toggles sorting on Space over a focused sortable header, and its React-root handler runs before the bubble-phase dispatcher — so Space is claimed in capture phase: sorting never toggles and the quick filter opens instead. Scoped to gridRef (not the wider scopeRef) so the deepest grid owning the focused header wins.
  useKeyboardShortcut({
    id: `${shortcutId}-sort-header-space`,
    scopeRef: gridRef,
    capture: true,
    shouldHandle: isSpaceOnSortableHeader,
    onTrigger: onSortHeaderSpace,
  });
};
